import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core'
import { HttpClient } from '@angular/common/http'
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms'
import { Store, select } from '@ngrx/store'
import { fromLonLat, toLonLat } from 'ol/proj'
import { containsCoordinate } from 'ol/extent'
import { of, Subject, Subscription } from 'rxjs'
import {
  catchError,
  debounceTime,
  filter,
  switchMap,
  map,
  tap,
} from 'rxjs/operators'
import { environment } from '@env/environment'
import { getSettingsState } from '@containers/settings/settings.selectors'

@Component({
  selector: 'map-coordinates-finder',
  templateUrl: './map-coordinates-finder.component.html',
  styleUrls: ['./map-coordinates-finder.component.css'],
})
export class MapCoordinatesFinderComponent implements OnInit, OnDestroy {
  @Input() public map: any
  @Output() public onClose: EventEmitter<any> = new EventEmitter()
  @Output() public onResultSelected: EventEmitter<any> = new EventEmitter()
  public searchMode: 'propertyCode' | 'buildingCode' | 'coordinates' =
    'propertyCode'
  public searchModeOptions = [
    { label: 'BY_PROPERTY_CODE', value: 'propertyCode' },
    { label: 'BY_BUILDING_CODE', value: 'buildingCode' },
    { label: 'BY_COORDINATES', value: 'coordinates' },
  ]
  public searchResults: any[] = []
  public isLoading = false
  public onSearchAutocomplete$ = new Subject<string>()
  public coordinatesForm: FormGroup
  public settings: any
  private search$: Subscription
  private settings$: Subscription
  private latSub$: Subscription
  private lngSub$: Subscription

  constructor(
    private http: HttpClient,
    private fb: FormBuilder,
    private store: Store<any>
  ) {}

  ngOnInit() {
    this.coordinatesForm = this.fb.group({
      lat: [
        null,
        [Validators.required, (control: AbstractControl) => this.latitudeValidator(control)],
      ],
      lng: [
        null,
        [Validators.required, (control: AbstractControl) => this.longitudeValidator(control)],
      ],
    })

    this.latSub$ = this.coordinatesForm.get('lat').valueChanges.subscribe(() => {
      this.coordinatesForm.get('lng').updateValueAndValidity({ emitEvent: false })
    })
    this.lngSub$ = this.coordinatesForm.get('lng').valueChanges.subscribe(() => {
      this.coordinatesForm.get('lat').updateValueAndValidity({ emitEvent: false })
    })

    this.settings$ = this.store
      .pipe(select(getSettingsState))
      .subscribe((state) => {
        this.settings = state.get('data')
      })

    this.search$ = this.onSearchAutocomplete$
      .pipe(
        filter((term) => !!term && term.length >= 2),
        debounceTime(400),
        tap(() => (this.isLoading = true)),
        switchMap((term) =>
          this.http
            .post<{
              data: {
                results: any[]
                pageSize: number
                totalCount: number
                filteredCount: number
              }
            }>(
              `${environment.API_ENDPOINT}/search/coordinatesByPropertyOrBuilding`,
              {
                filters: {
                  or: [{ field: this.searchMode, value: term }],
                },
                pageSize: 20,
                page: 0,
                sort: {},
              }
            )
            .pipe(
              map((res) => res.data.results),
              catchError(() => of([]))
            )
        ),
        tap(() => (this.isLoading = false))
      )
      .subscribe((results) => {
        this.searchResults = results
      })
  }

  ngOnDestroy(): void {
    this.search$.unsubscribe()
    this.settings$.unsubscribe()
    this.latSub$.unsubscribe()
    this.lngSub$.unsubscribe()
  }

  zoomToCoordinates(lat: number, lng: number): void {
    const coordinates = fromLonLat([lng, lat], 'EPSG:4326')
    this.map.getView().setCenter(coordinates)
    this.map.getView().setZoom(21)
  }

  setSearchMode(
    mode: 'propertyCode' | 'buildingCode' | 'coordinates'
  ): void {
    this.searchMode = mode
    this.searchResults = []
    if (mode === 'coordinates') {
      this.getMapCenterCoordinates()
    }
  }

  onResultSelect(item: any): void {
    if (item && item.latitude != null && item.longitude != null) {
      this.zoomToCoordinates(item.latitude, item.longitude)
      this.onResultSelected.emit(item)
      this.onClose.emit()
    }
  }

  getMapCenterCoordinates(): void {
    const center = this.map.getView().getCenter()
    if (center) {
      const [lng, lat] = toLonLat(center, 'EPSG:4326')
      this.coordinatesForm.patchValue({
        lat: parseFloat(lat.toFixed(6)),
        lng: parseFloat(lng.toFixed(6)),
      })
    }
  }

  onCoordinatesSubmit(): void {
    if (this.coordinatesForm.valid) {
      const { lat, lng } = this.coordinatesForm.value
      this.zoomToCoordinates(lat, lng)
      this.onResultSelected.emit({ latitude: lat, longitude: lng })
      this.onClose.emit()
    }
  }

  private latitudeValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const value = control.value
    if (value == null || value === '') return null
    if (value < -90 || value > 90) return { invalidCoordinates: true }
    if (!this.isWithinMapExtent([value, control.parent?.get('lng')?.value])) {
      return { invalidCoordinates: true }
    }
    return null
  }

  private longitudeValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const value = control.value
    if (value == null || value === '') return null
    if (value < -180 || value > 180) return { invalidCoordinates: true }
    if (!this.isWithinMapExtent([control.parent?.get('lat')?.value, value])) {
      return { invalidCoordinates: true }
    }
    return null
  }

  private isWithinMapExtent(coordinate: [number, number]): boolean {
    if (!this.settings?.size) return true
    const gis = this.settings.toJS().gis
    if (!gis?.GIS_MAP_EXTENT) return true
    const [lat, lng] = coordinate
    if (lat == null || lng == null) return true
    const extent = gis.GIS_MAP_EXTENT.split(',').map((c: string) => parseFloat(c))
    const projected = fromLonLat([lng, lat], 'EPSG:4326')
    return containsCoordinate(extent, projected)
  }
}
